import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { pickValue } from './restaurantApi';
import { normalizeIsDefault, normalizeTemplateSource } from './dailyMenuApi';
import MealTypesDropdown, { canonicalMealsCsv, parseMealsCsv } from './mealTypesDropdown';

export { MealTypesDropdown, canonicalMealsCsv, parseMealsCsv };

function authHeaders() {
    const token = localStorage.getItem('Token');
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export function normalizeMenuTemplateItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const isSelectedRaw = pickValue(raw, 'isSelected', 'is_selected', 'Is_Selected');
    let isSelected = true;
    if (isSelectedRaw === false || isSelectedRaw === 0 || isSelectedRaw === '0') {
        isSelected = false;
    } else if (isSelectedRaw === true || isSelectedRaw === 1 || isSelectedRaw === '1') {
        isSelected = true;
    } else if (isSelectedRaw != null) {
        isSelected = Boolean(isSelectedRaw);
    }

    return {
        localKey: pickValue(raw, 'localKey', '_localKey', 'local_key') ?? null,
        tmpItemId: pickValue(raw, 'tmpItemId', 'tmp_item_id', 'Tmp_Item_Id'),
        templateId: pickValue(raw, 'templateId', 'template_id', 'Template_Id'),
        foodItemId: pickValue(raw, 'foodItemId', 'food_item_id', 'Food_Item_Id'),
        foodItemName:
            pickValue(raw, 'foodItemName', 'food_item_name', 'Food_Item', 'food_item') ?? '',
        foodType: pickValue(raw, 'foodType', 'food_type', 'Food_Type') ?? '',
        foodCategory: pickValue(raw, 'foodCategory', 'food_category', 'Food_Category') ?? '',
        meal: pickValue(raw, 'meal', 'Meal') ?? '',
        price: pickValue(raw, 'price', 'Price') ?? '',
        basePrice: pickValue(raw, 'basePrice', 'base_price') ?? '',
        quantity: pickValue(raw, 'quantity', 'Quantity') ?? '',
        noOfItemsAvailable:
            pickValue(
                raw,
                'noOfItemsAvailable',
                'no_of_items_available',
                'No_of_items_available'
            ) ?? '',
        notes: pickValue(raw, 'notes', 'Notes') ?? '',
        isSelected,
        foodItemImageUrl:
            pickValue(raw, 'foodItemImageUrl', 'food_item_image_url', 'imageUrl', 'image_url') ??
            null,
        agePricing: Array.isArray(raw.agePricing ?? raw.age_pricing)
            ? (raw.agePricing ?? raw.age_pricing).map((tier) => ({
                  agePricingId: tier.agePricingId ?? tier.Age_Pricing_Id ?? null,
                  minAge: tier.minAge ?? tier.Min_Age ?? '',
                  maxAge: tier.maxAge ?? tier.Max_Age ?? '',
                  quantity: tier.quantity ?? tier.Quantity ?? '',
                  price: tier.price ?? tier.Price ?? '',
                  status: tier.status ?? tier.Status ?? 'ACTIVE',
              }))
            : [],
    };
}

export function normalizeMenuTemplateHeader(raw) {
    if (!raw || typeof raw !== 'object') return null;
    return {
        templateId: pickValue(raw, 'templateId', 'template_id', 'Template_Id'),
        restaurantId: pickValue(raw, 'restaurantId', 'restaurant_id', 'Restaurant_Id'),
        templateName: pickValue(raw, 'templateName', 'template_name', 'Template_Name') ?? '',
        templateSource: normalizeTemplateSource(
            pickValue(raw, 'templateSource', 'template_source', 'Template_Source')
        ),
        dayOfWeek: pickValue(raw, 'dayOfWeek', 'day_of_week', 'Day_Of_Week') ?? '',
        isDefault: normalizeIsDefault(
            pickValue(raw, 'isDefault', 'is_default', 'Is_Default')
        ),
        status: pickValue(raw, 'status', 'Status') ?? '',
        createdDate: pickValue(raw, 'createdDate', 'created_date', 'Created_Date') ?? '',
        updatedDate: pickValue(raw, 'updatedDate', 'updated_date', 'Updated_Date') ?? '',
        items: Array.isArray(raw.items)
            ? raw.items.map(normalizeMenuTemplateItem).filter(Boolean)
            : [],
    };
}

/** GET /restaurants/menu-templates/:templateId */
export async function fetchMenuTemplateById(templateId) {
    const response = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.restaurantMenuTemplateById(templateId),
        { headers: authHeaders() }
    );
    const data = response.data || {};
    const template = normalizeMenuTemplateHeader(data);
    const items =
        template?.items?.length > 0
            ? template.items
            : Array.isArray(data.items)
              ? data.items.map(normalizeMenuTemplateItem).filter(Boolean)
              : [];
    return { ...template, items };
}

/** POST /restaurants/menu-templates/:templateId/items */
export async function addMenuTemplateItems(templateId, items) {
    return axiosRestaurantInstance.post(
        RESTAURANT_API_PATHS.restaurantMenuTemplateItems(templateId),
        { items },
        { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
    );
}

/** PUT /restaurants/menu-templates/:templateId */
export async function updateMenuTemplate(templateId, payload) {
    return axiosRestaurantInstance.put(
        RESTAURANT_API_PATHS.restaurantMenuTemplateById(templateId),
        payload,
        { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
    );
}

/** PUT /restaurants/menu-templates/items/:tmpItemId */
export async function updateMenuTemplateItem(tmpItemId, payload) {
    return axiosRestaurantInstance.put(
        RESTAURANT_API_PATHS.restaurantMenuTemplateItemById(tmpItemId),
        payload,
        { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
    );
}

export function flattenMenuItems(menuItems) {
    if (!menuItems || typeof menuItems !== 'object') return [];
    return Object.entries(menuItems).flatMap(([foodType, list]) => {
        const items = Array.isArray(list) ? list : [];
        return items.map((item) => ({
            ...item,
            Food_Type: item.Food_Type ?? foodType,
            Food_Item_Id: item.Food_Item_Id ?? item.foodItemId,
            Food_Item: item.Food_Item ?? item.foodItem ?? '',
            Food_Category: item.Food_Category ?? item.foodCategory ?? '',
            Price: item.Price ?? item.price ?? '',
            Quantity: item.Quantity ?? item.quantity ?? '',
            imageUrl: item.imageUrl ?? item.foodItemImageUrl ?? null,
        }));
    });
}

export function defaultPickerRowDraft(menuRow = null) {
    const tiers = Array.isArray(menuRow?.agePricing) ? menuRow.agePricing : [];
    const tierPrice = tiers[0]?.price ?? tiers[0]?.Price;
    return {
        meal: '',
        noOfItemsAvailable: '50',
        notes: '',
        isSelected: true,
        price: String(tierPrice ?? menuRow?.Price ?? menuRow?.price ?? ''),
    };
}

export function getPickerRowDraft(drafts, foodId, menuRow = null) {
    return drafts[foodId] ?? defaultPickerRowDraft(menuRow);
}

export const emptyItemForm = () => ({
    foodItemId: '',
    noOfItemsAvailable: '50',
    price: '',
    meal: '',
    notes: '',
    isSelected: true,
    agePricing: [],
});

export function itemFormFromRow(item) {
    const row = normalizeMenuTemplateItem(item) || item;
    return {
        foodItemId: String(row.foodItemId ?? ''),
        noOfItemsAvailable: String(row.noOfItemsAvailable ?? 50),
        price: String(row.price ?? row.basePrice ?? ''),
        meal: canonicalMealsCsv(row.meal ?? ''),
        notes: String(row.notes ?? ''),
        isSelected: Boolean(row.isSelected),
        agePricing: Array.isArray(row.agePricing) ? row.agePricing : [],
    };
}

export function buildItemPayload(form, userId, restaurantId, isUpdate) {
    const actorId = Number(userId) || Number(restaurantId) || 1;
    const base = {
        foodItemId: Number(form.foodItemId),
        noOfItemsAvailable: Number(form.noOfItemsAvailable) || 0,
        price: Number(form.price) || 0,
        meal: String(form.meal ?? '').trim(),
        isSelected: form.isSelected ? 1 : 0,
        notes: String(form.notes ?? '').trim(),
    };
    return isUpdate ? { ...base, updatedById: actorId } : { ...base, createdById: actorId };
}

export function buildBulkAddPayload(menuRow, rowDraft, actorId) {
    return {
        foodItemId: Number(menuRow.Food_Item_Id ?? menuRow.foodItemId),
        noOfItemsAvailable: Number(rowDraft.noOfItemsAvailable) || 0,
        price: Number(rowDraft.price ?? menuRow.Price ?? menuRow.price) || 0,
        meal: String(rowDraft.meal ?? '').trim(),
        isSelected: rowDraft.isSelected ? 1 : 0,
        notes: String(rowDraft.notes ?? '').trim(),
        createdById: actorId,
    };
}

export function draftItemFromMenuRow(menuRow, rowDraft) {
    const payload = buildBulkAddPayload(menuRow, rowDraft, 0);
    const idForKey = payload.foodItemId;
    return normalizeMenuTemplateItem({
        localKey: `local-${idForKey}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        foodItemId: payload.foodItemId,
        foodItemName: menuRow.Food_Item ?? menuRow.foodItem ?? menuRow.Item_Name ?? '',
        foodCategory: menuRow.Food_Category ?? menuRow.foodCategory ?? '',
        foodType: menuRow.itemFoodType ?? menuRow.Food_Type ?? '',
        foodItemImageUrl: menuRow.imageUrl ?? menuRow.foodItemImageUrl ?? null,
        quantity: menuRow.Quantity ?? menuRow.quantity ?? '',
        agePricing: menuRow.agePricing ?? menuRow.age_pricing ?? [],
        meal: payload.meal,
        price: payload.price,
        noOfItemsAvailable: payload.noOfItemsAvailable,
        notes: payload.notes,
        isSelected: Boolean(payload.isSelected),
    });
}

export function itemToCreatePayload(item, actorId) {
    const row = normalizeMenuTemplateItem(item) || item;
    return buildItemPayload(itemFormFromRow(row), actorId, actorId, false);
}

export function applyTemplateItemsFromResponse(data) {
    const tpl = normalizeMenuTemplateHeader(data?.template ?? data);
    if (tpl && Array.isArray(tpl.items)) {
        return tpl.items;
    }
    return null;
}

export function templateItemUnifiedId(item) {
    const row = normalizeMenuTemplateItem(item) || item;
    return row.foodItemId ?? null;
}

export function getTemplateItemRowKey(item) {
    const row = normalizeMenuTemplateItem(item) || item;
    return (
        row.tmpItemId ??
        row.localKey ??
        `${row.foodItemId}-${row.meal}`
    );
}
